import {
    Box,
    Drawer,
    DrawerBody,
    DrawerContent,
    DrawerHeader,
    DrawerOverlay,
    Heading,
    HStack,
    IconButton,
    Menu,
    MenuButton,
    MenuItem,
    MenuList,
    Spacer,
    useBreakpointValue,
    useDisclosure,
} from '@chakra-ui/react';
import { faBell } from '@fortawesome/pro-light-svg-icons';
import { faXmark } from '@fortawesome/pro-regular-svg-icons';
import { FaIcon } from '@acp/core-ui';
import { faEllipsis } from '@fortawesome/sharp-light-svg-icons';
import { useExperiment } from '@acp/feature/auth';
import { Experiment } from '@acp/shared';
import { useAcknowledge, useMarkAllAsRead, useNotificationSummary } from '../hooks/use-notifications';
import { NotificationList } from './notification-list';

function NotificationBadge({ label }: { label: string }) {
    return (
        <Box
            position="absolute"
            top={2}
            left={7}
            bg="danger.500"
            color="white"
            borderRadius="full"
            minW={4}
            h={4}
            px={1}
            display="flex"
            alignItems="center"
            justifyContent="center"
            fontSize="xs"
            fontWeight="bold"
            cursor="pointer"
        >
            {label}
        </Box>
    );
}

function NotificationOptions({ shouldShow }: { shouldShow: boolean }) {
    const markAllAsRead = useMarkAllAsRead();

    if (shouldShow === false) return null;

    return (
        <Menu>
            <MenuButton
                as={IconButton}
                icon={<FaIcon icon={faEllipsis} />}
                variant="ghost"
                colorScheme="muted"
            />
            <MenuList>
                <MenuItem onClick={() => markAllAsRead.mutate()} fontSize="md">
                    Mark all as read
                </MenuItem>
            </MenuList>
        </Menu>
    );
}

function NotificationButtonInner() {
    const { data } = useNotificationSummary();
    const acknowledge = useAcknowledge();
    const { isOpen, onOpen, onClose } = useDisclosure();

    const drawerSize = useBreakpointValue<'full' | 'sm'>({
        base: 'full',
        md: 'sm',
    });

    const unseenCount = data?.unseenCount ?? 0;
    const hasUnread = (data?.unreadCount ?? 0) > 0;
    const hasUnseenNotifications = unseenCount > 0;
    const unseenCountLabel = unseenCount > 9 ? '9+' : `${unseenCount}`;

    const handleOpen = () => {
        acknowledge.mutate();
        onOpen();
    };

    return (
        <>
            <Box position="relative" display="inline-flex" onClick={handleOpen}>
                <IconButton
                    aria-label="Notifications"
                    size="lg"
                    fontSize={20}
                    variant="ghost"
                    color="black"
                    colorScheme="blackAlpha"
                    icon={<FaIcon icon={faBell} />}
                />
                {hasUnseenNotifications && (
                    <NotificationBadge label={unseenCountLabel} />
                )}
            </Box>

            <Drawer
                placement="right"
                size={drawerSize}
                isOpen={isOpen}
                onClose={onClose}
                returnFocusOnClose
                trapFocus={false}
                useInert={false}
                blockScrollOnMount={false}
            >
                <DrawerOverlay />
                <DrawerContent>
                    <DrawerHeader pr={2} borderBottomWidth={1} borderColor="muted.50">
                        {/* Match the height of the desktop navigation bar */}
                        <HStack h="47px">
                            <Heading size="md">
                                My notifications
                            </Heading>
                            <Spacer />
                            <HStack>
                                <NotificationOptions shouldShow={hasUnread} />
                                <IconButton
                                    aria-label="Close notifications"
                                    icon={<FaIcon icon={faXmark} />}
                                    variant="ghost"
                                    colorScheme="muted"
                                    size="md"
                                    onClick={onClose}
                                />
                            </HStack>
                        </HStack>
                    </DrawerHeader>
                    <DrawerBody overflowY="hidden" p={0}>
                        <NotificationList />
                    </DrawerBody>
                </DrawerContent>
            </Drawer>
        </>
    );
}

// Split so we don't fetch notification summaries for users who can't see the bell.
export function NotificationButton() {
    const { hasExperiment } = useExperiment();

    if (!hasExperiment(Experiment.AgencyCompliancePortalNotifications)) {
        return null;
    }

    return <NotificationButtonInner />;
}
